import { Directive, ElementRef, HostListener, Input, OnDestroy, inject } from '@angular/core';

/**
 * Drag-to-scroll for horizontal rails.
 *
 * Every carousel on the home page is an `overflow-x: auto` strip. Those are
 * fine on touch (native momentum scrolling) and fine with the arrow buttons,
 * but with a plain mouse there is no good way to move them — most mice have
 * no horizontal wheel, so the content is effectively stuck unless the user
 * finds the arrows. This adds click-and-drag panning, which is what every
 * major feed UI does.
 *
 * Usage: `<div class="my-track" dragScroll>`
 *
 * Deliberate behaviours:
 * - **Mouse only.** Touch and pen already scroll natively; hijacking them
 *   would fight the platform and feel worse, not better.
 * - **Threshold before engaging.** A drag is only recognised after the
 *   pointer moves DRAG_THRESHOLD px, so a normal click on a card still
 *   reaches the card. Without this, every click would be swallowed.
 * - **Click suppressed after a real drag.** Releasing at the end of a drag
 *   would otherwise fire a click on whatever card is under the cursor and
 *   navigate away — the single most annoying bug in hand-rolled drag rails.
 * - **Snap and smooth-scroll disabled mid-drag.** `scroll-snap-type` yanks
 *   the rail toward the nearest card on every frame, and `scroll-behavior:
 *   smooth` animates each assignment, so together they make direct
 *   scrollLeft writes stutter and fight the pointer. Both are restored on
 *   release, so snapping still applies once the drag ends.
 * - **Native image/link dragging blocked while panning.** Cards contain
 *   <img> and <a>, both of which start a browser drag-and-drop that
 *   immediately kills the pan.
 */
@Directive({
  selector: '[dragScroll]',
  standalone: true,
  host: {
    // Signals the affordance. Applied from the directive rather than each
    // component's stylesheet so there's a single place to change it, and so
    // adding the attribute is genuinely all a rail needs.
    '[style.cursor]': "'grab'",
    // Pointer dragging below is mouse-only. Touch and pen keep their native
    // horizontal/vertical panning instead of being restricted to pan-y.
    '[style.touch-action]': "'pan-x pan-y'",
  },
})
export class DragScrollDirective implements OnDestroy {
  /** Set to false to disable panning without removing the attribute. */
  @Input() dragScrollEnabled = true;

  /** Pointer travel (px) before a press is treated as a drag, not a click. */
  private static readonly DRAG_THRESHOLD = 6;

  private readonly el: HTMLElement = inject(ElementRef).nativeElement;

  private pointerId: number | null = null;
  private startX = 0;
  private startScrollLeft = 0;
  private dragging = false;

  // Saved so they can be restored exactly as the stylesheet had them.
  private prevSnap = '';
  private prevBehavior = '';
  private prevBodyCursor = '';

  ngOnDestroy(): void {
    this.teardown();
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(e: PointerEvent): void {
    if (!this.dragScrollEnabled) return;
    // Primary button, mouse only — see the class comment.
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    // Nothing to pan.
    if (this.el.scrollWidth <= this.el.clientWidth) return;

    this.pointerId = e.pointerId;
    this.startX = e.clientX;
    this.startScrollLeft = this.el.scrollLeft;
    this.dragging = false;
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(e: PointerEvent): void {
    if (this.pointerId === null || e.pointerId !== this.pointerId) return;

    const dx = e.clientX - this.startX;

    if (!this.dragging) {
      if (Math.abs(dx) < DragScrollDirective.DRAG_THRESHOLD) return;
      this.beginDrag();
    }

    // Chromium uses negative scrollLeft values for RTL overflow rails.
    // Dragging toward the left must therefore make the value more negative;
    // applying the LTR formula gets clamped at zero and looks completely
    // unresponsive. Resolve direction from the actual rail rather than from
    // the document because individual components can override it.
    const rtl = getComputedStyle(this.el).direction === 'rtl';
    // Rails should move in the direction users expect from a carousel drag:
    // reverse the viewport delta while still respecting RTL's negative
    // scrollLeft coordinate system.
    this.el.scrollLeft = this.startScrollLeft + (rtl ? -dx : dx);
    e.preventDefault();
  }

  @HostListener('pointerup', ['$event'])
  @HostListener('pointercancel', ['$event'])
  onPointerUp(e: PointerEvent): void {
    if (this.pointerId === null || e.pointerId !== this.pointerId) return;

    const wasDragging = this.dragging;
    this.teardown();

    if (wasDragging) this.suppressNextClick();
  }

  /** Native DnD on images/anchors would abort the pan mid-gesture. */
  @HostListener('dragstart', ['$event'])
  onDragStart(e: DragEvent): void {
    if (this.pointerId !== null) e.preventDefault();
  }

  // ── internals ───────────────────────────────────────────────────────────

  private beginDrag(): void {
    this.dragging = true;

    this.prevSnap = this.el.style.scrollSnapType;
    this.prevBehavior = this.el.style.scrollBehavior;
    this.el.style.scrollSnapType = 'none';
    this.el.style.scrollBehavior = 'auto';
    this.el.style.userSelect = 'none';

    // Cursor goes on <body> so it stays "grabbing" even while the pointer is
    // over a child card (which sets its own cursor) or outside the rail.
    this.prevBodyCursor = document.body.style.cursor;
    document.body.style.cursor = 'grabbing';

    // Keeps pointermove/up coming to this element once the cursor leaves it,
    // so dragging past the edge of the rail doesn't strand the gesture.
    try {
      if (this.pointerId !== null) this.el.setPointerCapture(this.pointerId);
    } catch {
      /* capture is best-effort; panning still works without it */
    }
  }

  private teardown(): void {
    if (this.pointerId !== null) {
      try {
        this.el.releasePointerCapture(this.pointerId);
      } catch {
        /* already released */
      }
    }

    if (this.dragging) {
      this.el.style.scrollSnapType = this.prevSnap;
      this.el.style.scrollBehavior = this.prevBehavior;
      this.el.style.userSelect = '';
      document.body.style.cursor = this.prevBodyCursor;
    }

    this.pointerId = null;
    this.dragging = false;
  }

  /**
   * Swallow the one click that the browser fires after the drag's pointerup,
   * so finishing a pan on top of a card doesn't also open that card.
   * Capture phase, so it runs before any card's own click handler.
   */
  private suppressNextClick(): void {
    const kill = (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
    };

    this.el.addEventListener('click', kill, { capture: true, once: true });

    // If no click follows (pointer released outside, gesture cancelled), the
    // once-listener would linger and eat a genuine click later. Drop it on
    // the next frame — the synthetic click always arrives before this.
    requestAnimationFrame(() =>
      this.el.removeEventListener('click', kill, { capture: true } as EventListenerOptions),
    );
  }
}
